import React, { createContext } from "react";
import { ResolvedThemeName } from "./common/styles/theme";
export type ThemeToggleContextValue = {
  /** The currently-resolved theme bucket ("light" | "dark"). */
  activeThemeName: ResolvedThemeName;
  /**
   * Whether the visitor is allowed to manually override the resolved
   * theme (mirrors `Brand.allowVisitorThemeOverride`, defaulting to
   * `true`). Consumers (e.g. `Header`'s theme toggle) should hide their
   * toggle control entirely when this is `false`, rather than disabling
   * it.
   */
  allowVisitorThemeOverride: boolean;
  /** Sets (and persists) the visitor's manual theme preference. */
  setVisitorOverride: (theme: ResolvedThemeName) => void;
};

/**
 * Exposes the app's resolved active theme bucket and a way to change it,
 * to descendants of `ContentContainer` (which owns the actual theme
 * resolution via `useActiveBrandTheme` — see `common/styles`). Mirrors the
 * `AppContext` pattern in `AppDataProvider.tsx` for consistency.
 *
 * The default value here (a no-op `setVisitorOverride`, "light" active
 * theme) is only ever used if a consumer is rendered outside of
 * `ContentContainer`'s provider (e.g. some isolated unit tests/stories) —
 * in real app usage this is always overridden by `ContentContainer`.
 */
export const ThemeToggleContext = createContext<ThemeToggleContextValue>({
  activeThemeName: "light",
  allowVisitorThemeOverride: true,
  setVisitorOverride: () => {}
});
export default ThemeToggleContext;